import { useDispatch, useSelector } from 'react-redux';
import _findIndex from 'lodash/findIndex';

import {
  AppState, LoadHeightState, PalletDataState, UnitDimensionsState, UnitWeightState,
} from 'interfaces/store';
import {
  jumpToStepCalculatorAction,
  nextStepCalculatorAction,
  prevStepCalculatorAction,
  set3DView,
  setDimensionsCalculatorAction,
  setLoadHeightCalculatorAction,
  setPalletDataCalculatorAction,
  setUnitPackagingCalculatorAction,
  setWeightCalculatorAction,
} from 'store/actions/calculator';
import {
  LoadHeightType, PalletType, UnitDimension, UnitPackaging, UnitWeight,
} from 'enums/calculator';
import { CONVERSION_RATE, MAX_LOAD_HEIGHT_MM } from 'config/calculator';

import { convertTo, isValidNumber, roundNumber } from '../utils/numbers';

type DimensionKey = keyof UnitDimensionsState;
type WeightKey = keyof UnitWeightState;
type PalletKey = keyof PalletDataState;
type LoadHeightKey = keyof LoadHeightState;

const useCalculatorState = () => {
  const dispatch = useDispatch();
  const {
    steps,
    currentStep,
    packagingType,
    dimensions,
    weight,
    palletData,
    height,
    view3D,
  } = useSelector(({ calculator }: AppState) => calculator);

  const nextStep = () => dispatch(nextStepCalculatorAction());
  const prevStep = () => dispatch(prevStepCalculatorAction());
  const isFirstStep = () => {
    const index = _findIndex(steps, { id: currentStep.id });
    return index === 0;
  };
  const isLastStep = () => {
    const index = _findIndex(steps, { id: currentStep.id });
    return index === steps.length - 1;
  };

  const jumpToStep = (stepIndex: number) => dispatch(jumpToStepCalculatorAction(stepIndex));

  const isFirstStepValid = () => !(
    dimensions.widthError
    || dimensions.heightError
    || dimensions.lengthError
    || weight.error
    || !isValidNumber(dimensions.width)
    || !isValidNumber(dimensions.height)
    || !isValidNumber(dimensions.length)
    || !isValidNumber(weight.value)
  );

  const isStep2Valid = () => !(
    palletData.widthError
    || palletData.heightError
    || palletData.lengthError
    || !isValidNumber(palletData.width)
    || !isValidNumber(palletData.height, true)
    || !isValidNumber(palletData.length)
  );

  const isStep4Valid = () => {
    const heightInMm = height.unit === UnitDimension.MM
      ? Number(height.value) : convertTo(Number(height.value), height.unit);
    let maxHeight = MAX_LOAD_HEIGHT_MM;
    if (palletData.height) {
      maxHeight = MAX_LOAD_HEIGHT_MM - palletData.height;
    }
    return heightInMm > 0 && heightInMm < maxHeight;
  };

  const setPackagingType = (type: UnitPackaging) => dispatch(
    setUnitPackagingCalculatorAction(type),
  );

  const setDimensionKey = (key: DimensionKey, value: UnitDimension | number | null) => {
    if (dimensions[key] === value) {
      return;
    }

    const newDimension = { ...dimensions, ...{ [key]: value } };
    dispatch(setDimensionsCalculatorAction(newDimension));
  };

  const setDimensionType = (value: UnitDimension) => setDimensionKey('type', value);
  const setDimensionWidth = (value: number | null) => setDimensionKey('width', value);
  const setDimensionHeight = (value: number | null) => setDimensionKey('height', value);
  const setDimensionLength = (value: number | null) => setDimensionKey('length', value);

  const setWeightKey = (key: WeightKey, value: UnitWeight | string | number | null) => {
    if (weight[key] === value) {
      return;
    }

    const newWeight = { ...weight, ...{ [key]: value } };
    dispatch(setWeightCalculatorAction(newWeight));
  };

  const setWeightType = (value: UnitWeight) => setWeightKey('type', value);
  const setWeightValue = (value: number | null) => setWeightKey('value', value);

  const setLoadHeightKey = (
    key: LoadHeightKey,
    value: LoadHeightState | UnitDimension | string | number | null,
  ) => {
    if (height[key] === value) {
      return;
    }

    const newHeight = { ...height, ...{ [key]: value } };
    dispatch(setLoadHeightCalculatorAction(newHeight, dimensions));
  };

  const setLoadHeightType = (value: LoadHeightType) => setLoadHeightKey('type', value);
  const setLoadHeightUnit = (value: UnitDimension) => setLoadHeightKey('unit', value);
  const setLoadHeightValue = (value: number | null) => {
    let layers;
    let heightValue;
    const packageHeight = dimensions.type === height.unit
      ? dimensions.height : convertTo(Number(dimensions.height), height.unit);

    if (height.type === LoadHeightType.HEIGHT) {
      heightValue = Number(value);

      layers = Math.floor(Number(value) / Number(packageHeight));
    } else {
      layers = Number(value);
      heightValue = roundNumber(Number(value) * Number(packageHeight));
    }
    const newHeight = { ...height, ...{ value: heightValue, layers } };
    dispatch(setLoadHeightCalculatorAction(newHeight, dimensions));
  };

  const setPalletData = (type: PalletType) => {
    let pallet;
    switch (type) {
      case PalletType.PALLET_40x54:
        pallet = {
          type: PalletType.PALLET_40x54,
          width: 40,
          length: 54,
          height: 6,
          unitType: UnitDimension.INCH,
        };
        break;
      case PalletType.INDUSTRIE:
        pallet = {
          type: PalletType.INDUSTRIE,
          width: 1200,
          length: 1000,
          height: 6 * CONVERSION_RATE,
          unitType: UnitDimension.MM,
        };
        break;
      case PalletType.EUROPALLET:
        pallet = {
          type: PalletType.EUROPALLET,
          width: 1200,
          length: 800,
          height: 6 * CONVERSION_RATE,
          unitType: UnitDimension.MM,
        };
        break;
      case PalletType.CUSTOM:
        pallet = {
          type: PalletType.CUSTOM,
          unitType: UnitDimension.INCH,
        };
        break;
      default:
        pallet = {
          type: PalletType.PALLET_40x48,
          width: 40,
          length: 48,
          height: 6,
          unitType: UnitDimension.INCH,
        };
    }
    dispatch(setPalletDataCalculatorAction(pallet));
  };

  const setPalletKey = (key: PalletKey, value: UnitDimension | number | null) => {
    if (palletData[key] === value) {
      return;
    }

    const newPallet = { ...palletData, ...{ [key]: value } };
    dispatch(setPalletDataCalculatorAction(newPallet));
  };

  const setPalletUnit = (value: UnitDimension) => setPalletKey('unitType', value);
  const setPalletWidth = (value: number | null) => setPalletKey('width', value);
  const setPalletHeight = (value: number | null) => setPalletKey('height', value);
  const setPalletLength = (value: number | null) => setPalletKey('length', value);
  const toggle3DView = () => dispatch(set3DView(!view3D));

  return {
    steps,
    currentStep,
    packagingType,
    dimensions,
    weight,
    palletData,
    height,
    view3D,

    isFirstStep,
    isLastStep,
    nextStep,
    prevStep,
    jumpToStep,
    isFirstStepValid,
    isStep2Valid,
    isStep4Valid,

    setPalletData,
    setPalletUnit,
    setPalletWidth,
    setPalletHeight,
    setPalletLength,
    setPackagingType,
    setDimensionType,
    setDimensionWidth,
    setDimensionHeight,
    setDimensionLength,
    setWeightType,
    setWeightValue,
    setLoadHeightType,
    setLoadHeightUnit,
    setLoadHeightValue,
    toggle3DView,
  };
};

export default useCalculatorState;
